import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';

import axios from 'axios';
import { useParams } from 'react-router-dom';

import Layout from 'containers/layouts/layout';
import AnalyseIcon from 'images/icons/combinations/analyseIcon.svg';
import AnalyseIconGray from 'images/icons/combinations/analyseIconGray2.svg';
import EvolutionIcon from 'images/icons/combinations/evolutionIcon.svg';
import EvolutionIconGray from 'images/icons/combinations/evolutionIconGray2.svg';
import OperationIcon from 'images/icons/combinations/operationIcon.svg';
import OperationIconGray from 'images/icons/combinations/operationIconGray2.svg';
import Environment from 'utils/environments';

import HomeCards from './components/homeCards';
import WelcomeBanner from './components/welcomeBanner';
import Modal from './modal';
import { DashboardContainer, CardsWrapper } from './style';
import OverlayFooter from 'containers/footerMobile/footer';
import ValidityProductCards from 'pages/home/labelHome/components/ValidityProductCards';
import CommonAlert from 'components/CommonAlert/CommonAlert';
import { LocalStyle } from 'pages/production/styles';

function OperacaoIcon({ variant }) {
  const src = variant === 'inativo' ? OperationIconGray : OperationIcon;
  return <img src={src} alt="Operação" width={64} height={58} />;
}

function AnaliseIcon({ variant }) {
  const src = variant === 'inativo' ? AnalyseIconGray : AnalyseIcon;
  return <img src={src} alt="Análise" width={64} height={58} />;
}

function EvolucaoIcon({ variant }) {
  const src = variant === 'inativo' ? EvolutionIconGray : EvolutionIcon;
  return <img src={src} alt="Evolução" width={64} height={58} />;
}

const ALL_STATUSES = ['EXPIRED', 'ON_DATE', 'VALID'];

function Dashboard() {
  const { id: restaurantId } = useParams();
  const environments = Environment(process.env.REACT_APP_ENV);
  const userInfo = useSelector((currState) => currState.user);

  const getTodayDateBR = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const today = getTodayDateBR();
  const [operationFetching, setOperationFetching] = useState(false);
  const [analiseFetching, setAnaliseFetching] = useState(false);
  const [evolutionFetching, setEvolutionFetching] = useState(false);
  const [operationDate, setOperationDate] = useState(today);
  const [analysisDate, setAnalysisDate] = useState(today);
  const [evolutionDate, setEvolutionDate] = useState(today);
  const [alerts, setAlerts] = useState(null);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [operationAlerts, setOperationAlerts] = useState(null);
  const [analysisAlerts, setAnalysisAlerts] = useState(null);
  const [evolutionAlerts, setEvolutionAlerts] = useState(null);
  const [validityCards, setValidityCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ description: '', status: '' });

  const toYesterdayBR = (brDate) => {
    if (!brDate) return '';
    const [d, m, y] = brDate.split('/').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() - 1);
    return new Intl.DateTimeFormat('pt-BR').format(dt);
  };

  const getCardVariant = (tasks) =>
    tasks?.some((task) => task.previsto && task.previsto.trim() !== '-')
      ? 'ativo'
      : 'inativo';

  const hasProductionRanges =
    Array.isArray(alerts?.operations?.productionTimeRanges) &&
    alerts.operations.productionTimeRanges.some(
      (r) => (r?.start || '').trim() && (r?.end || '').trim()
    );

  const hasLegacyProduction =
    !!alerts?.operations?.productionTimeStart &&
    !!alerts?.operations?.productionTimeEnd;

  const isOperationConfigured =
    (hasProductionRanges || hasLegacyProduction) &&
    !!alerts?.operations?.countingTimeStart &&
    !!alerts?.operations?.countingTimeEnd &&
    !!alerts?.operations?.salesTimeStart &&
    !!alerts?.operations?.salesTimeEnd &&
    !!alerts?.operations?.copilotTimeStart &&
    !!alerts?.operations?.copilotTimeEnd;

  const [operationData, setOperationData] = useState({
    loading: true,
    tasks: [],
    meta: {}
  });
  const [analiseData, setAnaliseData] = useState({
    loading: true,
    tasks: [],
    meta: {}
  });
  const [evolutionData, setEvolutionData] = useState({
    loading: true,
    tasks: [],
    meta: {}
  });

  const [modalState, setModalState] = useState({ isOpen: false, type: null });

  const handleOpenModal = (type) => setModalState({ isOpen: true, type });
  const handleCloseModal = () => setModalState({ isOpen: false, type: null });

  const isPastExpectedEnd = (endTime) => {
    if (!endTime) return false;
    const now = new Date();
    const [hour, minute] = endTime.split(':').map(Number);
    const end = new Date(now);
    end.setHours(hour, minute, 59, 999);
    return now > end;
  };

  useEffect(() => {
    async function fetchAlerts() {
      try {
        const response = await axios.get(
          `${environments.restaurants}/parameters/alerts/${restaurantId}`
        );

        const allAlerts = response.data.alerts || {};
        setAlerts(allAlerts);
        setOperationAlerts(allAlerts.operations || {});
        setAnalysisAlerts(allAlerts.analyse || {});
        setEvolutionAlerts(allAlerts.evolution || {});
      } catch (err) {
        console.error('Erro ao buscar alertas:', err);
      } finally {
        setAlertsLoading(false);
      }
    }

    if (restaurantId) fetchAlerts();
  }, [restaurantId]);

  useEffect(() => {
    const fetchEvolutionData = async () => {
      if (!evolutionDate || !restaurantId) return;

      setEvolutionFetching(true);

      try {
        const { data } = await axios.get(
          `${environments.catalog}/alerts/evolution`,
          {
            params: {
              date: evolutionDate,
              restaurantId,
              originId: restaurantId
            }
          }
        );

        const formatHorarioPrevisto = (start, end) =>
          start && end ? `${start} às ${end}` : '-';

        const formatRealTime = (realTime) => (realTime ? realTime : '-');

        setEvolutionData({
          loading: false,
          color: data.color,
          meta: {
            atingidas: data.targetStatus.split('/')[0],
            total: data.targetStatus.split('/')[1]
          },
          tasks: [
            {
              acao: 'Plano de ação Produções',
              previsto: formatHorarioPrevisto(
                data.productionActionPlan.expectedPlanTime?.start,
                data.productionActionPlan.expectedPlanTime?.end
              ),
              realizado:
                data.productionActionPlan.realPlanTime?.start &&
                data.productionActionPlan.realPlanTime?.end
                  ? `${data.productionActionPlan.realPlanTime.start} às ${data.productionActionPlan.realPlanTime.end}`
                  : '-',
              status:
                data.productionActionPlan.realPlanTime?.start &&
                data.productionActionPlan.realPlanTime?.end
                  ? data.productionActionPlan.color
                  : isPastExpectedEnd(
                      data.productionActionPlan.expectedPlanTime?.end
                    )
                  ? '#F53D4C'
                  : null
            },
            {
              acao: 'Plano de ação Contagens',
              previsto: formatHorarioPrevisto(
                data.countActionPlan.expectedPlanTime?.start,
                data.countActionPlan.expectedPlanTime?.end
              ),
              realizado:
                data.countActionPlan.realPlanTime?.start &&
                data.countActionPlan.realPlanTime?.end
                  ? `${data.countActionPlan.realPlanTime.start} às ${data.countActionPlan.realPlanTime.end}`
                  : '-',
              status:
                data.countActionPlan.realPlanTime?.start &&
                data.countActionPlan.realPlanTime?.end
                  ? data.countActionPlan.color
                  : isPastExpectedEnd(
                      data.countActionPlan.expectedPlanTime?.end
                    )
                  ? '#F53D4C'
                  : null
            },
            {
              acao: 'Projeção Vendas',
              previsto: formatHorarioPrevisto(
                data.sales.expectedPlanTime?.start,
                data.sales.expectedPlanTime?.end
              ),
              realizado: data.sales.realPlanTime || '-',
              status:
                data.sales.hasProjection && data.sales.realPlanTime
                  ? data.sales.color
                  : isPastExpectedEnd(data.sales.expectedPlanTime?.end)
                  ? '#F53D4C'
                  : null
            }
          ]
        });
      } catch (err) {
        console.error('Erro ao buscar evolução:', err);
      } finally {
        setEvolutionFetching(false);
      }
    };

    fetchEvolutionData();
  }, [evolutionDate, restaurantId, evolutionAlerts]);

  const getData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${environments.catalog}/printer/products/labels`,
        {
          params: { restaurantId }
        }
      );

      const responseData = res?.data || [];

      const mergedData = ALL_STATUSES?.map((status) => {
        const found = responseData?.find(
          (item) => item?.labelStatus === status
        );
        return {
          labelStatus: status,
          total: found?.total || 0
        };
      });

      setValidityCards(mergedData);
    } catch (error) {
      setMessage(
        error?.response?.data || {
          description: 'Erro ao carregar dados',
          status: 'error'
        }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [restaurantId]);

  const updateAlerts = useCallback(
    async (updatedSection) => {
      try {
        const normalizedSection = { ...updatedSection };

        if (modalState.type === 'operations') {
          if (Array.isArray(updatedSection.productionTimes)) {
            const prevRanges = updatedSection.productionTimeRanges || [];

            normalizedSection.productionTimeRanges =
              updatedSection.productionTimes
                .filter((t) => (t.start || '').trim() && (t.end || '').trim())
                .map((t, idx) => ({
                  _id: prevRanges[idx]?._id,
                  start: t.start,
                  end: t.end
                }));

            delete normalizedSection.productionTimes;
            delete normalizedSection.productionTimeStart;
            delete normalizedSection.productionTimeEnd;
          }
        }

        const updated = {
          ...alerts,
          [modalState.type]: normalizedSection
        };

        await axios.put(
          `${environments.restaurants}/parameters/alerts/${restaurantId}`,
          { alerts: updated }
        );

        setAlerts(updated);
        if (modalState.type === 'operations')
          setOperationAlerts(normalizedSection);
        if (modalState.type === 'analyse') setAnalysisAlerts(normalizedSection);
        if (modalState.type === 'evolution')
          setEvolutionAlerts(normalizedSection);
      } catch (err) {
        console.error('Erro ao atualizar alertas:', err);
      }
    },
    [alerts, modalState.type, restaurantId, environments.restaurants]
  );

  const formatHour = (isoString) => {
    if (!isoString) return '--';
    return new Date(isoString).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    const fetchOperationData = async () => {
      if (!operationDate || !restaurantId || alertsLoading) return;
      if (!operationAlerts) return;

      setOperationFetching(true);

      try {
        const { data } = await axios.get(
          `${environments.catalog}/alerts/operations`,
          {
            params: {
              date: operationDate,
              restaurantId,
              receivingRule: operationAlerts.receptionType || 'nf_date'
            }
          }
        );

        const formatReceptionType = (type) => {
          if (type === 'nf_date') return 'Data da integração';
          if (type === 'nf_date_plus_one') return 'Até 1 data integração';
          return '-';
        };

        let productionSource;
        if (Array.isArray(data.production) && data.production.length > 0) {
          productionSource = data.production;
        } else if (
          Array.isArray(operationAlerts?.productionTimeRanges) &&
          operationAlerts.productionTimeRanges.length > 0
        ) {
          productionSource = operationAlerts.productionTimeRanges.map((r) => ({
            startExpected: r?.start,
            endExpected: r?.end,
            startReal: null,
            endReal: null,
            color: null
          }));
        } else {
          productionSource = [{}];
        }

        const formatHorarioPrevisto = (start, end) =>
          start && end ? `${start} às ${end}` : '-';

        setOperationData({
          loading: false,
          color: data.color,
          meta: {
            atingidas: data.targetStatus.split('/')[0],
            total: data.targetStatus.split('/')[1]
          },
          tasks: [
            {
              acao: 'Recebimento',
              previsto: formatReceptionType(operationAlerts.receptionType),
              realizado: data.receiving?.displayValue || '-',
              status: data.receiving?.color || null
            },
            {
              acao: 'Copiloto',
              previsto: formatHorarioPrevisto(
                operationAlerts.copilotTimeStart,
                operationAlerts.copilotTimeEnd
              ),
              realizado:
                data.copilot?.first && data.copilot?.last
                  ? `${data.copilot.first} às ${data.copilot.last}`
                  : '-',
              status:
                data.copilot?.first && data.copilot?.last
                  ? data.copilot.color
                  : isPastExpectedEnd(operationAlerts?.copilotTimeEnd)
                  ? '#F53D4C'
                  : null
            },
            ...productionSource.map((range, index) => {
              const previsto =
                range.startExpected && range.endExpected
                  ? `${range.startExpected} às ${range.endExpected}`
                  : '-';

              const realizado =
                range.startReal && range.endReal
                  ? `${range.startReal} às ${range.endReal}`
                  : '-';

              const status =
                range.startReal && range.endReal
                  ? range.color
                  : isPastExpectedEnd(range.endExpected)
                  ? '#F53D4C'
                  : null;

              return {
                acao: `Produção ${index + 1}`,
                previsto,
                realizado,
                status
              };
            }),

            {
              acao: 'Contagem',
              previsto: formatHorarioPrevisto(
                operationAlerts.countingTimeStart,
                operationAlerts.countingTimeEnd
              ),
              realizado:
                data.count?.first && data.count?.last
                  ? `${formatHour(data.count.first)} às ${formatHour(
                      data.count.last
                    )}`
                  : '-',
              status:
                data.count?.first && data.count?.last
                  ? data.count.color
                  : isPastExpectedEnd(operationAlerts?.countingTimeEnd)
                  ? '#F53D4C'
                  : null,
              routeDate:
                operationAlerts?.countDateType === 'yesterday'
                  ? toYesterdayBR(operationDate)
                  : operationDate
            }
          ]
        });
      } catch (err) {
        console.error('Erro ao buscar operação:', err);
      } finally {
        setOperationFetching(false);
      }
    };

    fetchOperationData();
  }, [operationDate, restaurantId, alertsLoading, operationAlerts]);

  useEffect(() => {
    const fetchAnalysisData = async () => {
      if (!analysisDate || !restaurantId || alertsLoading) return;
      if (!analysisAlerts) return;

      setAnaliseFetching(true);

      try {
        const { data } = await axios.get(
          `${environments.catalog}/alerts/analysis`,
          { params: { date: analysisDate, restaurantId } }
        );

        const formatHorarioPrevisto = (start, end) =>
          start && end ? `${start} às ${end}` : '-';

        setAnaliseData({
          loading: false,
          color: data.color,
          meta: {
            atingidas: data.targetStatus.split('/')[0],
            total: data.targetStatus.split('/')[1]
          },
          tasks: [
            {
              acao: 'Produção',
              previsto: formatHorarioPrevisto(
                data.production.expected.start,
                data.production.expected.end
              ),
              realizado:
                data.production.real.start && data.production.real.end
                  ? formatHorarioPrevisto(
                      data.production.real.start,
                      data.production.real.end
                    )
                  : '-',
              status:
                data.production.real.start && data.production.real.end
                  ? data.production.color
                  : isPastExpectedEnd(data.production.expected.end)
                  ? '#F53D4C'
                  : null
            },
            {
              acao: 'Contagem',
              previsto: formatHorarioPrevisto(
                data.counting.expected.start,
                data.counting.expected.end
              ),
              realizado:
                data.counting.real.start && data.counting.real.end
                  ? formatHorarioPrevisto(
                      data.counting.real.start,
                      data.counting.real.end
                    )
                  : '-',
              status:
                data.counting.real.start && data.counting.real.end
                  ? data.counting.color
                  : isPastExpectedEnd(data.counting.expected.end)
                  ? '#F53D4C'
                  : null
            }
          ]
        });
      } catch (err) {
        console.warn('Erro ao buscar análise:', err);

        const formatHorarioPrevisto = (start, end) =>
          start && end ? `${start} às ${end}` : '-';

        setAnaliseData({
          loading: false,
          color: '#F53D4C',
          meta: { atingidas: '00', total: '02' },
          tasks: [
            {
              acao: 'Produção',
              previsto: formatHorarioPrevisto(
                analysisAlerts?.productionTimeStart,
                analysisAlerts?.productionTimeEnd
              ),
              realizado: '-',
              status: null
            },
            {
              acao: 'Contagem',
              previsto: formatHorarioPrevisto(
                analysisAlerts?.countingTimeStart,
                analysisAlerts?.countingTimeEnd
              ),
              realizado: '-',
              status: null
            }
          ]
        });
      } finally {
        setAnaliseFetching(false);
      }
    };

    fetchAnalysisData();
  }, [analysisDate, restaurantId, alertsLoading, analysisAlerts]);

  const operacaoVariant = getCardVariant(operationData.tasks);
  const analiseVariant = getCardVariant(analiseData.tasks);
  const evolucaoVariant = getCardVariant(evolutionData.tasks);

  return (
    <Layout>
      <LocalStyle />

      <DashboardContainer>
        <WelcomeBanner
          userName={userInfo.user}
          statusColors={{
            operacao:
              operacaoVariant === 'inativo' ? null : operationData.color,
            analise: analiseVariant === 'inativo' ? null : analiseData.color,
            evolucao: evolucaoVariant === 'inativo' ? null : evolutionData.color
          }}
          configuredCount={
            [operacaoVariant, analiseVariant, evolucaoVariant].filter(
              (v) => v === 'ativo'
            ).length
          }
        />

        <CardsWrapper>
          <HomeCards
            restaurantId={restaurantId}
            isLoading={operationData.loading || alertsLoading}
            isFetching={operationFetching}
            type="operacao"
            title="OPERAÇÃO"
            icon={
              <OperacaoIcon variant={getCardVariant(operationData.tasks)} />
            }
            meta={operationData.meta}
            tasks={operationData.tasks}
            selectedDate={operationDate}
            onDateChange={setOperationDate}
            onSettingsClick={() => handleOpenModal('operations')}
            isConfigured={isOperationConfigured}
            color={operationData.color}
          />

          <HomeCards
            restaurantId={restaurantId}
            isLoading={analiseData.loading || alertsLoading}
            isFetching={analiseFetching}
            type="analise"
            title="SUA ANÁLISE"
            icon={<AnaliseIcon variant={getCardVariant(analiseData.tasks)} />}
            meta={analiseData.meta}
            tasks={analiseData.tasks}
            selectedDate={analysisDate}
            onDateChange={setAnalysisDate}
            onSettingsClick={() => handleOpenModal('analyse')}
            isConfigured={isOperationConfigured}
            color={analiseData.color}
          />

          <HomeCards
            restaurantId={restaurantId}
            isLoading={evolutionData.loading || alertsLoading}
            isFetching={evolutionFetching}
            type="evolucao"
            title="EVOLUÇÃO"
            icon={
              <EvolucaoIcon variant={getCardVariant(evolutionData.tasks)} />
            }
            meta={evolutionData.meta}
            tasks={evolutionData.tasks}
            selectedDate={evolutionDate}
            onDateChange={setEvolutionDate}
            onSettingsClick={() => handleOpenModal('evolution')}
            isConfigured={isOperationConfigured}
            color={evolutionData.color}
          />
        </CardsWrapper>

        {/* <ValidityProductCards
          data={validityCards}
          loading={loading}
          customClassName="homeValidityCards"
        /> */}
      </DashboardContainer>

      <Modal
        isOpen={modalState.isOpen}
        onClose={handleCloseModal}
        type={modalState.type}
        data={alerts?.[modalState.type] || null}
        onSave={updateAlerts}
        restaurantId={restaurantId}
      />

      <CommonAlert
        open={message?.description}
        onClose={() => setMessage({ description: '', status: '' })}
        severity={message?.status}
        indexMessage={message?.description}
        messagePersonal={message?.description}
      />

      <OverlayFooter />
    </Layout>
  );
}

export default Dashboard;
